import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, ShieldCheck, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { SerpMark } from "@/components/SerpBrand";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      if (!token) { setError("Missing invite token in URL"); setChecking(false); return; }
      try {
        const { data } = await api.get(`/auth/invite/${encodeURIComponent(token)}`);
        setMeta(data);
        setName(data.name || "");
      } catch (e) {
        setError(e?.response?.data?.detail || "Invalid or expired invite link");
      } finally { setChecking(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, password, name });
      // Session lives in the HttpOnly cookie set by the API.
      await refresh();
      toast.success("Welcome to " + (data.company || "SERP") + "!");
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept invite");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] flex">
      <div className="flex-1 flex flex-col justify-center px-8 md:px-16 max-w-2xl">
        <Link to="/" className="mb-16 inline-flex items-center gap-3">
          <SerpMark size={44} />
          <div>
            <div className="font-heading font-black text-[#1A202C] text-2xl tracking-tight leading-none">SERP</div>
            <div className="text-[10px] uppercase tracking-widest text-[#1C1C1C] font-semibold mt-1">Shanvi Enterprise Resource Planning</div>
          </div>
        </Link>

        <div className="max-w-md w-full">
          {checking && (
            <div className="flex items-center gap-2 text-[#4A5568]" data-testid="accept-checking">
              <Loader2 size={16} className="animate-spin" /> Verifying invite…
            </div>
          )}

          {!checking && error && (
            <div data-testid="accept-error">
              <div className="overline">Invite</div>
              <h1 className="font-heading font-bold text-3xl tracking-tight mt-1 mb-2 flex items-center gap-3">
                <XCircle size={26} className="text-[#B42318]" /> Link isn&apos;t valid
              </h1>
              <p className="text-sm text-[#4A5568] mb-6">{error}</p>
              <Link to="/login" className="btn-primary"><ArrowRight size={14} /> Go to sign in</Link>
            </div>
          )}

          {!checking && meta && (
            <div data-testid="accept-form-wrap">
              <div className="overline">You&apos;ve been invited</div>
              <h1 className="font-heading font-bold text-3xl tracking-tight mt-1 mb-2">
                Join <span className="text-[#1C1C1C]">{meta.company_name}</span>
              </h1>
              <p className="text-sm text-[#4A5568] mb-6">
                Set your name and password to accept the invite for <span className="font-mono-data">{meta.email}</span>{" "}
                as a <span className="chip-info inline-flex items-center gap-1"><ShieldCheck size={11} /> {meta.role}</span>.
              </p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="label-field">Full name</label>
                  <input required value={name} onChange={(e) => setName(e.target.value)}
                    className="input-clean" style={{ fontFamily: "'Inter', sans-serif" }}
                    placeholder="Jane Miner" data-testid="accept-name" />
                </div>
                <div>
                  <label className="label-field">Choose a password</label>
                  <input required minLength={6} type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    className="input-clean" style={{ fontFamily: "'Inter', sans-serif" }}
                    placeholder="At least 6 characters" data-testid="accept-password" />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="accept-submit">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                  Accept & sign in
                </button>
              </form>
            </div>
          )}
        </div>
      </div>

      <div className="hidden lg:block flex-1 relative bg-[#F5F7FA] border-l border-[#DDE3ED] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: "linear-gradient(#FFFFFF 1px, transparent 1px), linear-gradient(90deg, #FFFFFF 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div
          className="absolute -right-32 -top-32 w-[520px] h-[520px] rounded-full opacity-25"
          style={{ background: "radial-gradient(closest-side, #1C1C1C 0%, transparent 70%)" }}
        />
      </div>
    </div>
  );
}
